import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  Zap,
  GitPullRequest,
  Users,
  Bell,
  Link2,
  BarChart3,
  Search,
  FileEdit,
  UserCheck,
  Send,
  ArrowRight,
  ChevronRight,
  CheckCircle,
  Mail,
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import Roles from '../constants/roles';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import configs from '../config';

const features = [
  {
    icon: Zap,
    title: 'AI Cover Letters',
    description: 'Generate personalized, high-converting cover letters powered by GPT-4 and Claude. Trained on your past proposals and portfolio.',
  },
  {
    icon: GitPullRequest,
    title: 'Proposal Pipeline',
    description: 'Track every proposal from draft to submitted. Review workflows, status tracking, and team approvals built in.',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Role-based permissions, shared proposal reviews, and centralized messaging for agencies and teams.',
  },
  {
    icon: Bell,
    title: 'Real-Time Notifications',
    description: 'Instant alerts for new jobs matching your filters, proposal updates, and client responses.',
  },
  {
    icon: Link2,
    title: 'Dual Upwork Access',
    description: 'Use full OAuth sync when you have client credentials, or switch to Browser-Assist mode to import profile pages and work directly from the Upwork extension.',
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description: 'Proposal success rates, response times, earnings trends, and team performance at a glance.',
  },
];

const steps = [
  {
    icon: Search,
    title: 'Connect or Import',
    description: 'Connect Upwork with OAuth for live sync, or import your freelancer profile pages through the extension when OAuth is not available.',
  },
  {
    icon: FileEdit,
    title: 'Qualify Jobs',
    description: 'Propel scores jobs against your synced or imported profile, explains fit, highlights red flags, and helps you focus on the best opportunities.',
  },
  {
    icon: UserCheck,
    title: 'Draft with Your Data',
    description: 'Generate proposals, rates, and screening answers using your profile context, portfolio proof, and saved proposal history.',
  },
  {
    icon: Send,
    title: 'Review and Send',
    description: 'Open the Chrome side panel, review the draft, fill the Upwork form, and submit manually with Propel tracking every step.',
  },
];

const PropelLogo = ({ className = 'w-8 h-8' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className={className}>
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, hasHydrated, user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState(null);

  useDocumentTitle('Propel - Launch Smarter. Win Faster.');

  useEffect(() => {
    if (hasHydrated && isAuthenticated && user) {
      if (user.role === 'owner') {
        navigate('/owner/tenants', { replace: true });
      } else if (user.role === Roles.ADMIN) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/jobs', { replace: true });
      }
    }
  }, [isAuthenticated, hasHydrated, user, navigate]);

  if (!hasHydrated) return null;
  if (isAuthenticated && user) return null;

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistLoading(true);
    setWaitlistError(null);
    try {
      const response = await fetch(`${configs.BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.error || 'Something went wrong');
      }
      setWaitlistSubmitted(true);
    } catch (err) {
      setWaitlistError(err.message);
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <PropelLogo />
              <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollTo('features')} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </button>
              <button onClick={() => scrollTo('how-it-works')} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                How It Works
              </button>
              <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Sign In
              </Link>
              <button
                onClick={() => scrollTo('early-access')}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Get Early Access
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-100">
              <div className="flex flex-col space-y-3 pt-4">
                <button onClick={() => scrollTo('features')} className="text-left px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-50">
                  Features
                </button>
                <button onClick={() => scrollTo('how-it-works')} className="text-left px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-50">
                  How It Works
                </button>
                <Link to="/login" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-md hover:bg-gray-50">
                  Sign In
                </Link>
                <button onClick={() => scrollTo('early-access')} className="mx-3 text-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
                  Get Early Access
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary-100 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 left-10 w-72 h-72 bg-secondary-100 rounded-full blur-3xl opacity-20" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-gray-900 tracking-tight">
            Launch Smarter.{' '}
            <span className="text-primary-600">Win Faster.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-gray-600 leading-relaxed">
            Your Upwork proposal command center. AI-powered cover letters, real-time notifications, and team collaboration — all in one dashboard.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollTo('early-access')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 text-base font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-600/25 transition-all hover:shadow-xl hover:shadow-primary-600/30"
            >
              Get Early Access
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button
              onClick={() => scrollTo('how-it-works')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 text-base font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* Early Access Signup */}
      <section id="early-access" className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-6">
            Coming Soon
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-3">
            Get early access
          </h2>
          <p className="text-gray-600 mb-8">
            Be the first to try Propel when we launch. Join the waitlist and we'll notify you.
          </p>

          {waitlistSubmitted ? (
            <div className="flex items-center justify-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-6 py-4">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">You're on the list! We'll be in touch soon.</span>
            </div>
          ) : (
            <>
              <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={waitlistLoading}
                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none transition-all disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={waitlistLoading}
                  className="px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
                </button>
              </form>
              {waitlistError && (
                <p className="mt-3 text-sm text-red-600">{waitlistError}</p>
              )}
            </>
          )}

          <p className="mt-4 text-xs text-gray-400">No spam, ever. We'll only email you about launch updates.</p>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900">
              Everything you need to win on Upwork
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
              From AI-generated proposals to real-time analytics, Propel gives your team the edge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group relative p-6 bg-white rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900">
              From profile access to final submit
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
              Propel supports both full OAuth sync and Browser-Assist mode, so the workflow stays smooth even when Upwork credentials are unavailable.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div key={step.title} className="relative text-center">
                {/* Connector line (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] border-t-2 border-dashed border-gray-300" />
                )}
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white shadow-md border border-gray-100 mb-6">
                  <step.icon className="w-8 h-8 text-primary-600" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 sm:py-24 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white">
            Ready to supercharge your Upwork career?
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            Join the waitlist and be first in line when Propel launches.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollTo('early-access')}
              className="inline-flex items-center px-8 py-3.5 text-base font-medium text-primary-700 bg-white rounded-xl hover:bg-gray-50 shadow-lg transition-all"
            >
              Join the Waitlist
              <ChevronRight className="ml-1.5 w-5 h-5" />
            </button>
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-3.5 text-base font-medium text-white border border-white/30 rounded-xl hover:bg-white/10 transition-all"
            >
              Already have access? Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-6 md:mb-0">
              <PropelLogo className="w-6 h-6" />
              <span className="text-lg font-heading font-bold text-white">Propel</span>
            </div>
            <div className="flex items-center space-x-8 mb-6 md:mb-0">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-gray-300 transition-colors">Privacy</Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-gray-300 transition-colors">Terms</Link>
              <Link to="/contact" className="text-sm text-gray-400 hover:text-gray-300 transition-colors">Contact</Link>
            </div>
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Propel. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
